/**
 * TaskDetailsAssignees Component
 *
 * Renders the assignees and collaborators management section including:
 * - Assignee Org selection (for non-project tasks)
 * - Assignee selection with org-based filtering
 * - Collaborators management
 * - Edit mode with save/cancel actions
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Row, Col, Tooltip, Select, Divider, message } from 'antd';
import {
  EditOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import '../../styles/index.scss';

const { Option } = Select;

const TaskDetailsAssignees = ({
  currentTask,
  currentUser,
  level,
  isEditingAssignee,
  setIsEditingAssignee,
  selectedOrg,
  setSelectedOrg,
  selectedAssignees,
  setSelectedAssignees,
  selectedCollaborators,
  setSelectedCollaborators,
  isCollaborator,
  isCurrentUserAssignee,
  isFollowers,
  updateTaskLoading,
  basicOrgsDetails,
  basicOrgsDetailsLoading,
  basicUsersDetails,
  basicUsersDetailsLoading,
  collaboratorsData,
  collaboratorsLoading,
  fetchBasicOrgs,
  fetchBasicUsers,
  fetchCollaborators,
  onUserUpdate,
  onCollaboratorsUpdate,
  onFollowerSelected,
}) => {
  return (
    <Card className="task-assignees__card">
      {/* Assigned to Header */}
      <Col className="task-assignees__section-header">
        <Text strong className="task-assignees__section-title">
          Assigned to
        </Text>
        {!isEditingAssignee && level === 'PLATFORM' && (
          <EditOutlined
            className="task-assignees__edit-icon"
            onClick={() => setIsEditingAssignee(true)}
          />
        )}
      </Col>

      {/* Assignee Org (only for non-project tasks) */}
      {!currentTask?.project && (
        <Col className="task-assignees__field">
          <Text strong className="task-assignees__label">
            Assignee Org
          </Text>
          {isEditingAssignee ? (
            <Select
              className="custom-select"
              value={selectedOrg}
              style={{ width: '100%' }}
              onChange={(value) => {
                setSelectedOrg(value);
                setSelectedAssignees([]);
              }}
              loading={basicOrgsDetailsLoading}
              placeholder="Select Organization"
              onSearch={(e) => fetchBasicOrgs(e)}
              onFocus={() => fetchBasicOrgs()}
            >
              {basicOrgsDetails
                .concat(
                  currentTask &&
                    currentTask.assigneeOrg &&
                    !basicOrgsDetails.some((org) => org.id === currentTask.assigneeOrg.id)
                    ? [currentTask.assigneeOrg]
                    : []
                )
                .map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
            </Select>
          ) : (
            <Text className="task-assignees__value">
              {currentTask && currentTask.assigneeOrg ? currentTask.assigneeOrg.name : '-'}
            </Text>
          )}
        </Col>
      )}

      {/* Assignee */}
      <Col className="task-assignees__field task-assignees__field--overflow">
        <Text strong className="task-assignees__label">
          Assignee
          {currentTask?.project && (
            <Text className="task-assignees__label-hint">
              (from project team)
            </Text>
          )}
        </Text>
        {isEditingAssignee ? (
          <>
          <Select
            className="custom-select"
            value={selectedAssignees}
            loading={basicUsersDetailsLoading}
            style={{ width: '100%' }}
            placeholder={
              currentTask?.project
                ? 'Select from project team'
                : selectedOrg
                ? 'Select Assignee(s)'
                : 'Select an organization first'
            }
            showSearch
            allowClear
            disabled={!currentTask?.project && !selectedOrg}
            onSearch={(e) => !currentTask?.project && fetchBasicUsers(e)}
            onChange={(selectedIds) => {
              const followers = currentTask?.followers || [];
              const followerIds = followers.map((user) => user.id);
              if (followerIds.includes(selectedIds)) {
                onFollowerSelected(selectedIds);
              } else {
                setSelectedAssignees(selectedIds);
              }
            }}
            onFocus={() => !currentTask?.project && fetchBasicUsers()}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) => {
              return optionA.children
                .toLowerCase()
                .localeCompare(optionB.children.toLowerCase());
            }}
          >
            {currentTask?.project ? (
              <>
                {currentTask.project.owner && (
                  <Option key={currentTask.project.owner.id} value={currentTask.project.owner.id}>
                    {startCase(currentTask.project.owner.name || currentTask.project.owner.username)}
                  </Option>
                )}
                {currentTask.project.team &&
                  currentTask.project.team
                    .filter((user) => user.id !== currentTask.project.owner?.id)
                    .map((user) => (
                      <Option key={user.id} value={user.id}>
                        {startCase(user.name || user.username)}
                      </Option>
                    ))}
              </>
            ) : (
              selectedOrg &&
              basicUsersDetails
                .concat(
                  currentTask?.assigneeUsers?.filter(
                    (u) => !basicUsersDetails.some((bu) => bu.id === u.id)
                  ) || []
                )
                .map((user) => (
                  <Option key={user.id} value={user.id}>
                    {startCase(user.name || user.username)}
                  </Option>
                ))
            )}
          </Select>
          {!currentTask?.project && !selectedOrg && (
            <Text className="task-assignees__helper-text">
              Select an organization above to see available assignees
            </Text>
          )}
          </>
        ) : (
          <Text className="task-assignees__value">
            {currentTask &&
            currentTask.assigneeUsers &&
            currentTask.assigneeUsers.length > 0
              ? currentTask.assigneeUsers.map((user, index) => (
                  <Row key={index} className="task-assignees__assignee-row">
                    {startCase(user.name || user.username)}
                  </Row>
                ))
              : startCase(
                  currentTask && currentTask.assigneeUsers && currentTask.assigneeUsers.name
                ) || '-'}
          </Text>
        )}
      </Col>

      <Col>
        <Divider />
      </Col>

      {/* Collaborators Header */}
      <Col className="task-assignees__collaborators-header">
        <Col className="task-assignees__collaborators-title-row">
          <UserOutlined className="task-assignees__collaborators-icon" />
          <Text strong className="task-assignees__section-title">
            Collaborators
          </Text>
        </Col>
        <Col>
          {level === 'PLATFORM' && !isEditingAssignee && (
            <Button
              className="task-assignees__action-btn"
              disabled={updateTaskLoading}
              onClick={onCollaboratorsUpdate}
            >
              {isCollaborator ? (
                <>
                  <MinusOutlined className="task-assignees__action-icon task-assignees__action-icon--leave" />
                  <Text className="task-assignees__action-text task-assignees__action-text--leave">Leave</Text>
                </>
              ) : (
                <>
                  <PlusOutlined className="task-assignees__action-icon task-assignees__action-icon--join" />
                  <Text className="task-assignees__action-text task-assignees__action-text--join">Join</Text>
                </>
              )}
            </Button>
          )}
        </Col>
      </Col>

      {/* Collaborators List or Edit */}
      {!isEditingAssignee ? (
        <Col>
          {currentTask &&
          currentTask.collaborators &&
          currentTask.collaborators.length > 0 ? (
            <div className="task-assignees__collaborators-list">
              {currentTask.collaborators.map((collaborator, idx) => (
                <Tooltip
                  key={collaborator.id || idx}
                  title={startCase(collaborator.name || collaborator.username)}
                >
                  <div className="task-assignees__collaborator-item">
                    <AvatarS3
                      key={collaborator.id || idx}
                      imgKey={collaborator.avatar && collaborator.avatar.key}
                      username={collaborator.name || collaborator.username}
                      avatarProps={{ size: 40 }}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : (
            <Card className="task-assignees__empty-card">
              <Text>
                Collaborators receive the same alerts as the assignee and can actively help
                complete the task.
              </Text>
            </Card>
          )}
        </Col>
      ) : (
        <>
          <Col>
            <Select
              className="custom-select"
              mode="multiple"
              value={selectedCollaborators}
              loading={collaboratorsLoading}
              style={{ width: '100%' }}
              placeholder="Select collaborator(s)"
              onSearch={(e) => fetchCollaborators(e)}
              onChange={(selectedIds) => {
                setSelectedCollaborators(selectedIds);
              }}
              onFocus={() => fetchCollaborators()}
              filterOption={false}
              filterSort={(optionA, optionB) => {
                return optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase());
              }}
            >
              {collaboratorsData.map((user) => (
                <Option key={user.id} value={user.id}>
                  {startCase(user.name || user.username)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Divider />
          </Col>
          <div className="task-assignees__edit-actions">
            <Button
              type="primary"
              onClick={onUserUpdate}
              disabled={updateTaskLoading}
              className="task-assignees__save-btn"
            >
              {updateTaskLoading && <LoadingOutlined />} Save
            </Button>
            <Button
              onClick={() => {
                setIsEditingAssignee(false);
                setSelectedOrg(
                  currentTask && currentTask.assigneeOrg && currentTask.assigneeOrg.id
                    ? currentTask.assigneeOrg.id
                    : null
                );
                setSelectedAssignees(
                  (currentTask &&
                    currentTask.assigneeUsers &&
                    currentTask.assigneeUsers.map((user) => user.id)) ||
                    []
                );
                setSelectedCollaborators(
                  currentTask &&
                    currentTask.collaborators &&
                    currentTask.collaborators.length > 0
                    ? currentTask.collaborators.map((user) => user.id)
                    : []
                );
              }}
              className="task-assignees__cancel-btn"
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

TaskDetailsAssignees.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    assigneeOrg: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    assigneeUsers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
    })),
    collaborators: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
      avatar: PropTypes.shape({
        key: PropTypes.string,
      }),
    })),
    followers: PropTypes.array,
    project: PropTypes.shape({
      id: PropTypes.string,
      owner: PropTypes.object,
      team: PropTypes.array,
    }),
  }),
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  level: PropTypes.string,
  isEditingAssignee: PropTypes.bool.isRequired,
  setIsEditingAssignee: PropTypes.func.isRequired,
  selectedOrg: PropTypes.string,
  setSelectedOrg: PropTypes.func.isRequired,
  selectedAssignees: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  setSelectedAssignees: PropTypes.func.isRequired,
  selectedCollaborators: PropTypes.array,
  setSelectedCollaborators: PropTypes.func.isRequired,
  isCollaborator: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isFollowers: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  basicOrgsDetails: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetails: PropTypes.array,
  basicUsersDetailsLoading: PropTypes.bool,
  collaboratorsData: PropTypes.array,
  collaboratorsLoading: PropTypes.bool,
  fetchBasicOrgs: PropTypes.func.isRequired,
  fetchBasicUsers: PropTypes.func.isRequired,
  fetchCollaborators: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func.isRequired,
  onCollaboratorsUpdate: PropTypes.func.isRequired,
  onFollowerSelected: PropTypes.func.isRequired,
};

TaskDetailsAssignees.defaultProps = {
  currentTask: null,
  level: null,
  selectedOrg: null,
  selectedAssignees: [],
  selectedCollaborators: [],
  isCollaborator: false,
  isCurrentUserAssignee: false,
  isFollowers: false,
  updateTaskLoading: false,
  basicOrgsDetails: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetails: [],
  basicUsersDetailsLoading: false,
  collaboratorsData: [],
  collaboratorsLoading: false,
};

export default TaskDetailsAssignees;
